import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { SearchOutlined, CheckOutlined } from "@ant-design/icons";
import "../../../renderer/patterns.css"; // Import pattern CSS classes
import { transformColor } from "../../../core/utils/generalUtils";
import {
  isJsPattern,
  getJsPatternComponent,
} from "../../../renderer/patterns-js";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

// Pattern metadata with display names and color requirements.
// ORDER matters — the UI iterates over Object.keys() to build the grid.
const PATTERN_METADATA = {
  // ── Featured ──
  "solid-primary": { name: "Solid", colors: 1, dynamic: false },
  "radial-shade": { name: "Radial Shade", colors: 2, dynamic: false },
  "horizontal-shade": { name: "Horizontal Shade", colors: 2, dynamic: false },
  "vertical-shade": { name: "Vertical Shade", colors: 2, dynamic: false },
  "vertical-shade-4": { name: "Vertical Shade 4", colors: 4, dynamic: false },
  "diagonal-shade-right": { name: "Diagonal Shade Right", colors: 2, dynamic: false },
  "diagonal-shade-left": { name: "Diagonal Shade Left", colors: 2, dynamic: false },
  "diagonal-frame-fade": { name: "Diagonal Frame Fade", colors: 4, dynamic: false, js: true },
  "blur": { name: "Blur", colors: 3, dynamic: false },
  "circles": { name: "Circles", colors: 4, dynamic: false },
  "split-frame-fade": { name: "Split Frame Fade", colors: 4, dynamic: false },

  // ── Waves & flow ──
  "blur-2": { name: "Blur 2", colors: 3, dynamic: false },
  "blur-3": { name: "Blur 3", colors: 4, dynamic: false },
  "blur-4": { name: "Blur 4", colors: 4, dynamic: false },
  "blur-5": { name: "Blur 5", colors: 3, dynamic: false },
  "wavy": { name: "Wavy", colors: 2, dynamic: false },
  "wavy-vibes": { name: "Wavy Vibes", colors: 1, dynamic: false },
  "short-waves": { name: "Short Waves", colors: 3, dynamic: false },
  "diagonal-waves": { name: "Diagonal Waves", colors: 2, dynamic: false },
  "liquid-wave": { name: "Liquid Wave", colors: 1, dynamic: false },
  "water-lines": { name: "Water Lines", colors: 2, dynamic: true, noColorShift: true },

  // ── Stripes & diagonals ──
  "stripes": { name: "Stripes", colors: 3, dynamic: false },
  "stripes-with-lights": { name: "Stripes with Lights", colors: 4, dynamic: false, noColorShift: true },
  "diagonals": { name: "Diagonals", colors: 2, dynamic: false },
  "diagonal-vanishin": { name: "Diagonal Vanishin", colors: 1, dynamic: false },
  "diagonal-arrows": { name: "Diagonal Arrows", colors: 1, dynamic: false },
  "varying-stripes": { name: "Varying Stripes", colors: 0, dynamic: false },
  "zigzag": { name: "Zigzag", colors: 3, dynamic: false },
  "digital-scanlines": { name: "Digital Scanlines", colors: 2, dynamic: false },

  // ── Shapes & geometry ──
  "split-squares": { name: "Split Squares", colors: 1, dynamic: false },
  "half-circles": { name: "Half Circles", colors: 2, dynamic: false },
  "honeycomb": { name: "Honeycomb", colors: 2, dynamic: false },
  "striped-circles": { name: "Striped Circles", colors: 2, dynamic: false },
  "corner-circles": { name: "Corner Circles", colors: 1, dynamic: false },
  "concentric-dashed": { name: "Concentric Dashed", colors: 1, dynamic: false },
  "concentric-dashed-translucent": { name: "Concentric Dashed Translucent", colors: 1, dynamic: false },
  "isometric-cubes": { name: "Isometric Cubes", colors: 4, dynamic: false },
  "kaleidoscope-radial": { name: "Kaleidoscope Radial", colors: 2, dynamic: false },
  "mandala-radial": { name: "Mandala Radial", colors: 3, dynamic: false },
  "retro-grid": { name: "Retro Grid", colors: 2, dynamic: false },
  "large-triangles": { name: "Large Triangles", colors: 1, dynamic: false },
  "citrus-shapes": { name: "Citrus Shapes", colors: 3, dynamic: false },

  // ── Squares & grids ──
  "full-squares": { name: "Full Squares", colors: 3, dynamic: false },
  "opache-squares": { name: "Opache Squares", colors: 3, dynamic: false },
  "organized-squares": { name: "Organized Squares", colors: 1, dynamic: false },
  "square-sizes": { name: "Square Sizes", colors: 1, dynamic: false },
  "irish-squares": { name: "Irish Squares", colors: 1, dynamic: false },
  "grid-hexagon": { name: "Grid Hexagon", colors: 0, dynamic: false },
  "grid-rectangle": { name: "Grid Rectangle", colors: 0, dynamic: false },

  // ── Textures & surfaces ──
  "woven-fabric": { name: "Woven Fabric", colors: 2, dynamic: false },
  "distorted-mesh": { name: "Distorted Mesh", colors: 2, dynamic: false },
  "tablecloth": { name: "Tablecloth", colors: 2, dynamic: false },
  "lisbon": { name: "Lisbon", colors: 2, dynamic: false },
  "topography": { name: "Topography", colors: 1, dynamic: false },
  "topographic-contours": { name: "Topographic Contours", colors: 3, dynamic: false },

  // ── Nature & themed ──
  "pineapple": { name: "Pineapple", colors: 3, dynamic: false },
  "pine-trees": { name: "Pine Trees", colors: 3, dynamic: false },
  "flat-mountains": { name: "Flat Mountains", colors: 0, dynamic: false },
  "pink-lotus": { name: "Pink Lotus", colors: 0, dynamic: false },
  "signal": { name: "Signal", colors: 2, dynamic: false },
  "confetti-doodles": { name: "Confetti Doodles", colors: 1, dynamic: false },
  "dalmatian-spots": { name: "Dalmatian Spots", colors: 1, dynamic: false },
  "circuit-board": { name: "Circuit Board", colors: 2, dynamic: false },
  "floating-cogs": { name: "Floating Cogs", colors: 1, dynamic: false },
  "network-connections": { name: "Network Connections", colors: 1, dynamic: false },
  "skulls": { name: "Skulls", colors: 1, dynamic: false },

  // ── Animated ──
  "particle-network": { name: "Particle Network", colors: 3, dynamic: true, js: true },
  "dot-globe": { name: "Dot Globe", colors: 3, dynamic: true, js: true },
  "dot-globe-corner": { name: "Dot Globe Corner", colors: 3, dynamic: true, js: true },
  "dot-globe-corner-left": { name: "Dot Globe Corner Left", colors: 3, dynamic: true, js: true },
  "aurora-ribbons": { name: "Aurora Ribbons", colors: 3, dynamic: true, js: true },
  "flow-field": { name: "Flow Field", colors: 3, dynamic: true, js: true },
  "synthwave-grid": { name: "Synthwave Grid", colors: 4, dynamic: true, js: true },
  "starfield-warp": { name: "Starfield Warp", colors: 2, dynamic: true, js: true },
  "liquid-blobs": { name: "Liquid Blobs", colors: 4, dynamic: true, js: true },
  "wireframe-terrain": { name: "Wireframe Terrain", colors: 3, dynamic: true, js: true },
  "galaxy-spiral": { name: "Galaxy Spiral", colors: 3, dynamic: true, js: true },
  "falling-petals": { name: "Falling Petals", colors: 4, dynamic: true, js: true },
  "plasma-waves": { name: "Plasma Waves", colors: 3, dynamic: true, js: true },
  "matrix-rain": { name: "Matrix Rain", colors: 3, dynamic: true, js: true },
  "hex-pulse": { name: "Hex Pulse", colors: 3, dynamic: true, js: true },
  "voronoi-cells": { name: "Voronoi Cells", colors: 4, dynamic: true, js: true },
  fireflies: { name: "Fireflies", colors: 3, dynamic: true, js: true },
  "ripple-pool": { name: "Ripple Pool", colors: 3, dynamic: true, js: true },
  "lightning-storm": { name: "Lightning Storm", colors: 3, dynamic: true, js: true },
  "bokeh-drift": { name: "Bokeh Drift", colors: 4, dynamic: true, js: true },
  "constellation-map": { name: "Constellation Map", colors: 3, dynamic: true, js: true },
  spirograph: { name: "Spirograph", colors: 4, dynamic: true, js: true },
  snowfall: { name: "Snowfall", colors: 3, dynamic: true, js: true },
  "comet-trails": { name: "Comet Trails", colors: 3, dynamic: true, js: true },
  "soap-bubbles": { name: "Soap Bubbles", colors: 4, dynamic: true, js: true },
  "cube-tumble": { name: "Cube Tumble", colors: 3, dynamic: true, js: true },
  "lava-lamp": { name: "Lava Lamp", colors: 4, dynamic: true, js: true },
  "mandala-bloom": { name: "Mandala Bloom", colors: 3, dynamic: true, js: true },
  "boids-flock": { name: "Boids Flock", colors: 3, dynamic: true, js: true },
  "dna-helix": { name: "DNA Helix", colors: 4, dynamic: true, js: true },
  "rain-on-glass": { name: "Rain on Glass", colors: 4, dynamic: true, js: true },
  "wave-interference": { name: "Wave Interference", colors: 3, dynamic: true, js: true },
  "atom-orbits": { name: "Atom Orbits", colors: 4, dynamic: true, js: true },
  "fourier-series": { name: "Fourier Series", colors: 4, dynamic: true, js: true },
  "martingale-walks": { name: "Martingale Walks", colors: 4, dynamic: true, js: true },
  "lorenz-attractor": { name: "Lorenz Attractor", colors: 4, dynamic: true, js: true },
  "phyllotaxis": { name: "Phyllotaxis", colors: 4, dynamic: true, js: true },
  "game-of-life": { name: "Game of Life", colors: 4, dynamic: true, js: true },
  "double-pendulum": { name: "Double Pendulum", colors: 4, dynamic: true, js: true },
  "fireworks": { name: "Fireworks", colors: 4, dynamic: true, js: true },
  "tessellation-wave": { name: "Tessellation Wave", colors: 4, dynamic: true, js: true },
  "equalizer-bars": { name: "Equalizer Bars", colors: 4, dynamic: true, js: true },
  "dynamic-square-circle": { name: "Dynamic Square Circle", colors: 2, dynamic: true },
  "dynamic-gradient-shift": { name: "Dynamic Gradient Shift", colors: 4, dynamic: true },
  "dynamic-moving-squares": { name: "Dynamic Moving Squares", colors: 2, dynamic: true },
  "dynamic-gradient-wave": { name: "Dynamic Gradient Wave", colors: 2, dynamic: true },
  "dynamic-rotating-dots-grid": { name: "Dynamic Rotating Dots Grid", colors: 4, dynamic: true },
  "rotating-half-circle-lines": { name: "Rotating Half Circle Lines", colors: 2, dynamic: true, js: true },
  "dynamic-diagonal-stripes": { name: "Dynamic Diagonal Stripes", colors: 4, dynamic: true },
  "dynamic-book-shelf": { name: "Book Shelf", colors: 4, dynamic: true },
  "moving-gradient-orb": { name: "Moving Gradient Orb", colors: 4, dynamic: true },
  "aurora-lights": { name: "Aurora Lights", colors: 0, dynamic: true, js: true },
  "aurora-colorful-lights": { name: "Aurora Colorful Lights", colors: 4, dynamic: true, js: true },
  "road-runner": { name: "Road Runner", colors: 4, dynamic: true },
  "dynamic-blur-drift": { name: "Dynamic Blur Drift", colors: 4, dynamic: true },
  "dynamic-blur-pulse": { name: "Dynamic Blur Pulse", colors: 3, dynamic: true },

  // ── Fixed colors (no user color choice) ──
  "spiral-rainy-drops": { name: "Spiral Rainy Drops", colors: 0, dynamic: false },
  "spiral-solar-drops": { name: "Spiral Solar Drops", colors: 0, dynamic: false },
  "green-opaque-squares": { name: "Green Opaque Squares", colors: 0, dynamic: false },
  "green-blue-dragon-scales": { name: "Green Blue Dragon Scales", colors: 0, dynamic: false },
  "joint-triangles": { name: "Joint Triangles", colors: 0, dynamic: false },
  "green-squares": { name: "Green Squares", colors: 0, dynamic: false },
  "pink-squares": { name: "Pink Squares", colors: 0, dynamic: false },
  "purple-dragon-scales": { name: "Purple Dragon Scales", colors: 0, dynamic: false },
  "slanted-gradient": { name: "Green Slanted Gradient", colors: 0, dynamic: false },
  "lunar-intersection": { name: "Lunar Intersection", colors: 0, dynamic: false },
  "solar-intersection": { name: "Solar Intersection", colors: 0, dynamic: false },
  "radiant-gradient": { name: "Radiant Gradient", colors: 0, dynamic: false },

  // ── Pushed to end ──
  "cloudy-day": { name: "Cloudy Day", colors: 4, dynamic: true },
  "night-clouds": { name: "Night Clouds", colors: 4, dynamic: true },
  "pigments": { name: "Pigments", colors: 2, dynamic: false },
  "confetti-brazil": { name: "Confetti Brazil", colors: 0, dynamic: false },
  "confetti-usa": { name: "Confetti USA", colors: 0, dynamic: false },
};

// Legacy/deprecated keys that might still be present in persisted websites.
// We normalize them for display/selection so the editor always highlights a valid option.
const LEGACY_PATTERN_KEY_ALIASES = {
  // Renames
  "night-clouds-moon-stars": "night-clouds",
  "clouds-and-sun": "cloudy-day",
  "quantum-grid": "water-lines",
  "cyberpunk-neon-grid": "road-runner",
  // Removed patterns (fallback)
  "star-pattern": "solid-primary",
  "starfield": "solid-primary",
  "plasma-blend": "solid-primary",
  "neon-hex": "solid-primary",
  "weathered-plaster": "solid-primary",
  "crumpled-paper": "solid-primary",
  "pi-circles": "solid-primary",
};

const normalizePatternKey = (patternKey) => {
  if (!patternKey) return patternKey;
  return LEGACY_PATTERN_KEY_ALIASES[patternKey] || patternKey;
};

// Helper function to check if all colors are the same
const areAllColorsSame = (colors) => {
  if (!colors || colors.length === 0) return false;
  const firstColor = colors[0];
  return colors.every(color => color === firstColor);
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to convert RGB to hex
const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

// Helper function to generate darker tone of a color
const generateDarkerTone = (hexColor, darknessFactor = 0.2) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;
  
  const darkerR = Math.max(0, Math.floor(rgb.r * (1 - darknessFactor)));
  const darkerG = Math.max(0, Math.floor(rgb.g * (1 - darknessFactor)));
  const darkerB = Math.max(0, Math.floor(rgb.b * (1 - darknessFactor)));
  
  return rgbToHex(darkerR, darkerG, darkerB);
};

// Helper function to generate CSS custom properties for pattern colors
const generatePatternColorVariables = (colors, pattern) => {
  const metadata = PATTERN_METADATA[pattern];

  if (!metadata || metadata.colors === 0) {
    return {};
  }

  // Normalize incoming colors (e.g., Tailwind tokens) to HEX for consistent previews
  const normalizedColors = Array.isArray(colors)
    ? colors.map((c) => {
        try {
          const hex = transformColor(c, 100, "hex");
          return hex;
        } catch (err) {
          return "#ffffff";
        }
      })
    : [];

  const cssVariables = {};
  const maxColors = Math.min(normalizedColors.length, metadata.colors);
  
  // Check if all colors are the same
  const allSame = areAllColorsSame(normalizedColors);
  const disableShift = metadata?.noColorShift === true;
  const baseColor = normalizedColors[0] || '#ffffff';
  
  for (let i = 0; i < maxColors; i++) {
    if (allSame && !disableShift && i > 0) {
      // Generate progressively darker tones for the same color
      const darknessFactor = 0.2 * i; // Each subsequent color gets darker
      cssVariables[`--color-${i + 1}`] = generateDarkerTone(baseColor, darknessFactor);
    } else {
      cssVariables[`--color-${i + 1}`] = normalizedColors[i] || '#ffffff';
    }
  }
  
  return cssVariables;
};

// Filter categories
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "static", label: "Static" },
  { key: "dynamic", label: "Animated" },
  { key: "fixed", label: "Fixed Colors" },
];

const RECENT_PATTERNS_STORAGE_KEY = "portfolioCreator.recentPatterns.v1";
const RECENT_PATTERNS_UPDATED_EVENT = "portfolioCreator:recent-patterns-updated";
const RECENT_PATTERN_TILE_MIN_WIDTH = 110;
const RECENT_PATTERN_TILE_GAP = 8;
const RECENT_PATTERN_FALLBACK_CAPACITY = 4;
const RECENT_PATTERN_LIMIT = 12;

const getRecentPatterns = () => {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_PATTERNS_STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizePatternKey)
      .filter((patternKey, index, list) =>
        PATTERN_METADATA[patternKey] && list.indexOf(patternKey) === index,
      )
      .slice(0, RECENT_PATTERN_LIMIT);
  } catch (_) {
    return [];
  }
};

const saveRecentPattern = (patternKey) => {
  const normalizedPatternKey = normalizePatternKey(patternKey);
  if (!normalizedPatternKey || !PATTERN_METADATA[normalizedPatternKey]) return getRecentPatterns();

  const nextPatterns = [
    normalizedPatternKey,
    ...getRecentPatterns().filter((recentPatternKey) => recentPatternKey !== normalizedPatternKey),
  ].slice(0, RECENT_PATTERN_LIMIT);

  try {
    window.localStorage.setItem(RECENT_PATTERNS_STORAGE_KEY, JSON.stringify(nextPatterns));
    window.dispatchEvent(
      new CustomEvent(RECENT_PATTERNS_UPDATED_EVENT, { detail: nextPatterns }),
    );
  } catch (_) {}

  return nextPatterns;
};

// Builds the JS layer element for a pattern when applicable. Used in the
// option preview tile and the selected-pattern bar so JS-powered patterns
// render their actual visual in the picker, not just the CSS color shell.
const buildJsPreviewLayer = (patternKey, currentColors) => {
  if (!isJsPattern(patternKey)) return null;
  const Component = getJsPatternComponent(patternKey);
  if (!Component) return null;

  const normalizedColors = Array.isArray(currentColors)
    ? currentColors.slice(0, 4).map((c) => {
        try {
          return transformColor(c, 100, "hex");
        } catch (err) {
          return null;
        }
      }).filter(Boolean)
    : [];

  return <Component colors={normalizedColors} patternKey={patternKey} />;
};

// Pattern option component (extracted outside for performance)
const PatternOption = React.memo(({ patternKey, isSelected, currentColors, onClick }) => {
  const metadata = PATTERN_METADATA[patternKey];
  const colorVars = generatePatternColorVariables(currentColors, patternKey);
  const jsLayer = metadata?.js ? buildJsPreviewLayer(patternKey, currentColors) : null;

  return (
    <button
      type="button"
      className="pf-option-btn"
      data-selected={isSelected || undefined}
      onClick={() => onClick(patternKey)}
      title={metadata.name}
    >
      {/* Pattern live preview — two-layer: outer clips, inner renders pattern */}
      <div className="pf-preview">
        <div
          className={`pf-preview-inner ${patternKey}`}
          style={colorVars}
        >
          {jsLayer}
        </div>
      </div>

      {/* Selection check badge */}
      {isSelected && (
        <span className="pf-check">
          <CheckOutlined style={{ fontSize: 10 }} />
        </span>
      )}

      {/* Pattern label */}
      <span className="pf-label">{metadata.name}</span>

      {/* Tags row */}
      <span className="pf-tags">
        {metadata.dynamic && <span className="pf-tag pf-tag-anim">Animated</span>}
        {metadata.colors > 0
          ? <span className="pf-tag pf-tag-colors">{metadata.colors} {metadata.colors === 1 ? "color" : "colors"}</span>
          : <span className="pf-tag pf-tag-fixed">Fixed</span>}
      </span>
    </button>
  );
});
PatternOption.displayName = "PatternOption";

export const PatternField = ({ value, onChange, label, help, currentColors = [] }) => {
  const normalizedValue = normalizePatternKey(value);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [recentPatterns, setRecentPatterns] = useState(() => getRecentPatterns());
  const [recentCapacity, setRecentCapacity] = useState(RECENT_PATTERN_FALLBACK_CAPACITY);
  const gridRef = useRef(null);
  const recentRowRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleRecentPatternsUpdated = (event) => {
      setRecentPatterns(Array.isArray(event?.detail) ? event.detail : getRecentPatterns());
    };

    setRecentPatterns(getRecentPatterns());
    window.addEventListener(RECENT_PATTERNS_UPDATED_EVENT, handleRecentPatternsUpdated);
    window.addEventListener("storage", handleRecentPatternsUpdated);

    return () => {
      window.removeEventListener(RECENT_PATTERNS_UPDATED_EVENT, handleRecentPatternsUpdated);
      window.removeEventListener("storage", handleRecentPatternsUpdated);
    };
  }, []);

  useEffect(() => {
    const rowElement = recentRowRef.current;
    if (!rowElement || typeof window === "undefined") return undefined;

    const updateCapacity = () => {
      const width = rowElement.clientWidth || rowElement.getBoundingClientRect?.().width || 0;
      if (!width) {
        setRecentCapacity(RECENT_PATTERN_FALLBACK_CAPACITY);
        return;
      }
      const capacity = Math.floor(
        (width + RECENT_PATTERN_TILE_GAP) /
        (RECENT_PATTERN_TILE_MIN_WIDTH + RECENT_PATTERN_TILE_GAP),
      );
      setRecentCapacity(Math.max(1, capacity));
    };

    updateCapacity();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCapacity);
      return () => window.removeEventListener("resize", updateCapacity);
    }

    const observer = new ResizeObserver(updateCapacity);
    observer.observe(rowElement);
    return () => observer.disconnect();
  }, [recentPatterns.length]);

  // Filter patterns
  const filteredPatterns = useMemo(() => {
    const allKeys = Object.keys(PATTERN_METADATA);
    
    // Apply category filter
    let filtered;
    switch (activeTab) {
      case "static":
        filtered = allKeys.filter(k => !PATTERN_METADATA[k].dynamic && PATTERN_METADATA[k].colors > 0);
        break;
      case "dynamic":
        filtered = allKeys.filter(k => PATTERN_METADATA[k].dynamic);
        break;
      case "fixed":
        filtered = allKeys.filter(k => PATTERN_METADATA[k].colors === 0);
        break;
      default:
        filtered = allKeys;
    }

    // Apply search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(k => PATTERN_METADATA[k].name.toLowerCase().includes(q));
    }

    return filtered;
  }, [activeTab, search]);

  // Count per tab (for badges)
  const counts = useMemo(() => {
    const all = Object.keys(PATTERN_METADATA);
    return {
      all: all.length,
      static: all.filter(k => !PATTERN_METADATA[k].dynamic && PATTERN_METADATA[k].colors > 0).length,
      dynamic: all.filter(k => PATTERN_METADATA[k].dynamic).length,
      fixed: all.filter(k => PATTERN_METADATA[k].colors === 0).length,
    };
  }, []);

  const visibleRecentPatterns = useMemo(
    () => recentPatterns.slice(0, recentCapacity),
    [recentCapacity, recentPatterns],
  );

  const handleSelect = useCallback((key) => {
    const normalizedKey = normalizePatternKey(key);
    const nextRecentPatterns = saveRecentPattern(normalizedKey);
    setRecentPatterns(nextRecentPatterns);
    onChange(normalizedKey);
  }, [onChange]);

  // Currently selected pattern meta (for top preview)
  const selectedMeta = normalizedValue ? PATTERN_METADATA[normalizedValue] : null;
  const selectedColorVars = normalizedValue ? generatePatternColorVariables(currentColors, normalizedValue) : {};
  const selectedJsLayer = selectedMeta?.js
    ? buildJsPreviewLayer(normalizedValue, currentColors)
    : null;

  return (
    <div className="field-wrapper">
      {label && <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>{label}</label>}

      <div className={`pf-root border ${borders.default} ${surfaces.primary} rounded-xl overflow-hidden`}>

        {/* ── Currently selected (sticky top bar) ── */}
        {selectedMeta && (
          <div className={`pf-selected-bar ${surfaces.secondary} border-b ${borders.default}`}>
            <div className="pf-selected-preview">
              <div
                className={`pf-preview-inner ${normalizedValue}`}
                style={selectedColorVars}
              >
                {selectedJsLayer}
              </div>
            </div>
            <div className="pf-selected-info">
              <span className={`pf-selected-name ${textColors.primary}`}>{selectedMeta.name}</span>
              <span className={`pf-selected-detail ${textColors.tertiary}`}>
                {selectedMeta.dynamic ? "Animated" : "Static"}
                {selectedMeta.colors > 0 ? ` \u00B7 ${selectedMeta.colors} colors` : " \u00B7 Fixed colors"}
              </span>
            </div>
          </div>
        )}

        {visibleRecentPatterns.length > 0 && (
          <div className={`pf-recent-section border-b ${borders.default}`}>
            <div className={`pf-recent-title ${typography.caption} ${textColors.tertiary}`}>
              Recently Used
            </div>
            <div
              className="pf-recent-row"
              ref={recentRowRef}
              style={{
                gridTemplateColumns: `repeat(${visibleRecentPatterns.length}, minmax(0, ${RECENT_PATTERN_TILE_MIN_WIDTH}px))`,
              }}
            >
              {visibleRecentPatterns.map((patternKey) => (
                <PatternOption
                  key={`recent-${patternKey}`}
                  patternKey={patternKey}
                  isSelected={normalizedValue === patternKey}
                  currentColors={currentColors}
                  onClick={handleSelect}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Search ── */}
        <div className={`pf-search-wrap border-b ${borders.default}`}>
          <SearchOutlined className={`pf-search-icon ${textColors.muted}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patterns\u2026"
            className={`pf-search-input ${surfaces.primary} ${textColors.primary}`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className={`pf-search-clear ${textColors.muted}`}
            >
              &times;
            </button>
          )}
        </div>

        {/* ── Category tabs ── */}
        <div className={`pf-tabs ${surfaces.secondary} border-b ${borders.default}`}>
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`pf-tab ${activeTab === tab.key ? "pf-tab-active" : ""}`}
            >
              {tab.label}
              <span className="pf-tab-count">{counts[tab.key]}</span>
            </button>
          ))}
        </div>

        {/* ── Pattern grid ── */}
        <div className="pf-grid-scroll" ref={gridRef}>
          {filteredPatterns.length === 0 ? (
            <div className={`pf-empty ${textColors.tertiary}`}>
              No patterns match &ldquo;{search}&rdquo;
            </div>
          ) : (
            <div className="pf-grid">
              {filteredPatterns.map(patternKey => {
                const isSelected = normalizedValue === patternKey;
                return (
                  <div key={patternKey}>
                    <PatternOption
                      patternKey={patternKey}
                      isSelected={isSelected}
                      currentColors={currentColors}
                      onClick={handleSelect}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}

      {/* Scoped styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* ── Root ── */
          .pf-root {
            display: flex;
            flex-direction: column;
          }

          /* ── Selected bar ── */
          .pf-selected-bar {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
          }
          .pf-selected-preview {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            overflow: hidden;
            flex-shrink: 0;
            position: relative;
            isolation: isolate;
          }
          .pf-selected-info {
            display: flex;
            flex-direction: column;
            min-width: 0;
          }
          .pf-selected-name {
            font-size: 13px;
            font-weight: 600;
            line-height: 1.25;
          }
          .pf-selected-detail {
            font-size: 11px;
            line-height: 1.3;
          }

          /* ── Recently used ── */
          .pf-recent-section {
            padding: 8px 10px 10px;
          }
          .pf-recent-title {
            margin-bottom: 6px;
            font-weight: 600;
          }
          .pf-recent-row {
            display: grid;
            gap: ${RECENT_PATTERN_TILE_GAP}px;
            justify-content: start;
            overflow: hidden;
          }
          .pf-recent-row .pf-option-btn {
            max-width: ${RECENT_PATTERN_TILE_MIN_WIDTH}px;
            min-width: 0;
          }

          /* ── Search ── */
          .pf-search-wrap {
            display: flex;
            align-items: center;
            padding: 0 12px;
            gap: 8px;
          }
          .pf-search-icon {
            flex-shrink: 0;
            font-size: 14px;
          }
          .pf-search-input {
            flex: 1;
            border: none;
            outline: none;
            padding: 8px 0;
            font-size: 13px;
            background: transparent;
          }
          .pf-search-clear {
            flex-shrink: 0;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 18px;
            line-height: 1;
            padding: 0 2px;
          }

          /* ── Tabs ── */
          .pf-tabs {
            display: flex;
            gap: 2px;
            padding: 6px 8px;
            overflow-x: auto;
          }
          .pf-tab {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 4px 10px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
            background: transparent;
            color: inherit;
            white-space: nowrap;
            transition: background 150ms, color 150ms;
          }
          :root[class*="dark"] .pf-tab,
          .dark .pf-tab {
            color: rgba(255,255,255,0.85);
          }
          .pf-tab:hover {
            background: rgba(0,0,0,0.05);
          }
          :root[class*="dark"] .pf-tab:hover,
          .dark .pf-tab:hover {
            background: rgba(255,255,255,0.07);
          }
          .pf-tab-active {
            background: linear-gradient(to right, #2563eb, #0ea5e9) !important;
            color: #fff !important;
          }
          .pf-tab-count {
            font-size: 10px;
            opacity: 0.7;
          }

          /* ── Grid scroll area ── */
          .pf-grid-scroll {
            max-height: 340px;
            overflow-y: auto;
            padding: 10px;
          }

          /* ── Grid ── */
          .pf-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
            gap: 8px;
          }

          /* ── Empty state ── */
          .pf-empty {
            text-align: center;
            padding: 32px 12px;
            font-size: 13px;
          }

          /* ── Option button ── */
          .pf-option-btn {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            border-radius: 8px;
            padding: 6px;
            border: 2px solid transparent;
            background: transparent;
            cursor: pointer;
            transition: border-color 150ms, box-shadow 150ms, transform 100ms;
          }
          .pf-option-btn:hover {
            border-color: rgba(37,99,235,0.35);
            transform: translateY(-1px);
          }
          :root[class*="dark"] .pf-option-btn:hover,
          .dark .pf-option-btn:hover {
            border-color: rgba(96,165,250,0.35);
          }
          .pf-option-btn[data-selected] {
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
          }
          :root[class*="dark"] .pf-option-btn[data-selected],
          .dark .pf-option-btn[data-selected] {
            border-color: #60a5fa;
            box-shadow: 0 0 0 3px rgba(96,165,250,0.12);
          }

          /* ── Preview (clipping wrapper) ── */
          .pf-preview {
            width: 100%;
            aspect-ratio: 16 / 10;
            border-radius: 6px;
            overflow: hidden;
            position: relative;
            isolation: isolate;
          }
          /* Inner layer that actually renders the pattern class.
             !important overrides the explicit height/width/margin/padding
             that many pattern CSS files set (e.g. height:100vh, height:700px). */
          .pf-preview-inner {
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            height: 100% !important;
            min-height: 0 !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            border-radius: 0 !important;
          }

          /* ── Check badge ── */
          .pf-check {
            position: absolute;
            top: 4px;
            right: 4px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #2563eb, #0ea5e9);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 1px 3px rgba(0,0,0,0.25);
            z-index: 2;
          }

          /* ── Label ── */
          .pf-label {
            display: block;
            font-size: 11px;
            font-weight: 500;
            margin-top: 4px;
            line-height: 1.3;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
          }
          :root[class*="dark"] .pf-label,
          .dark .pf-label {
            color: rgba(255,255,255,0.85);
          }

          /* ── Tags ── */
          .pf-tags {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
            margin-top: 2px;
            flex-wrap: wrap;
          }
          .pf-tag {
            font-size: 9px;
            font-weight: 500;
            padding: 1px 5px;
            border-radius: 4px;
            line-height: 1.5;
          }
          .pf-tag-anim {
            background: #fef3c7;
            color: #92400e;
          }
          :root[class*="dark"] .pf-tag-anim,
          .dark .pf-tag-anim {
            background: rgba(251,191,36,0.15);
            color: #fbbf24;
          }
          .pf-tag-colors {
            background: #dbeafe;
            color: #1e40af;
          }
          :root[class*="dark"] .pf-tag-colors,
          .dark .pf-tag-colors {
            background: rgba(59,130,246,0.15);
            color: #93c5fd;
          }
          .pf-tag-fixed {
            background: #f3f4f6;
            color: #6b7280;
          }
          :root[class*="dark"] .pf-tag-fixed,
          .dark .pf-tag-fixed {
            background: rgba(255,255,255,0.08);
            color: #9ca3af;
          }
        `
      }} />
    </div>
  );
};
